import React, {useCallback, useEffect, useState} from 'react';
import type {Account} from "../../types/Account";
import {Button, Grid, Theme, Typography} from "@mui/material";
import PartnerAddFundingSourceDialog from "../PartnerAddFundingSourceDialog";
import PartnerFundingSourcesList from "../PartnerFundingSourcesList";
import RemovePartnerFundingSourceDialog from "../RemovePartnerFundingSourceDialog";
import {withSnackbar} from "../hocs/withSnackbar";
import {makeStyles} from '@mui/styles';
import {requestLoggedInAuthoritySource} from "../../services/session/session-requests";
import PartnerVerifyBankAccountDialog from "./PartnerVerifyBankAccountDialog";
import {requestGetFundingSourceId} from "./request/payment-method-requests";
import {useFlags} from "launchdarkly-react-client-sdk";
import EmailVerifyToolTip from "../notifications/EmailVerifyToolTip";

const useStyles: (theme: Theme) => {
  gap15: CSSStyleSheet,
  addPaymentButton: CSSStyleSheet,
} = makeStyles(() => ({
  gap15: {
    gap: '1.07rem'
  },
  addPaymentButton: {
    textTransform: 'uppercase'
  },
}));

const PartnerPaymentMethods = (props: {
  account: Account,
  handleLogout: () => void,
  handleAccountUpdated: () => void,
  snackbarShowMessage: () => void
}) => {

  const {account, handleLogout, handleAccountUpdated, snackbarShowMessage} = props;
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showDeletePaymentDialog, setShowDeletePaymentDialog] = useState(false);
  const [showVerifyPaymentDialog, setShowVerifyPaymentDialog] = useState(false);
  const [fundSourceId, setFundSourceId] = useState('');
  const fundingSourceExists = account.existingFundingSourceBankName && account.existingFundingSourceBankAccountType;

  const classes = useStyles();
  const flags = useFlags();
  const isMicroDepositVerificationEnabled = flags.microdepositVerification;

  const handleFundingSourceToVerify = (fundingSource) => {
    setFundSourceId(fundingSource);
  }

  useEffect(() => {
    requestGetFundingSourceId(account.id, handleFundingSourceToVerify, snackbarShowMessage);
  }, [account]);

  const openAddPaymentModal = () => {
    setShowAddPaymentModal(true);
  };

  const RefreshAuthoritySourceAndUser = () => {
    requestLoggedInAuthoritySource().then((resp) => {
      handleAccountUpdated(resp.body);
    }).catch(error => {
      if (error.status === 401) {
        handleLogout();
      } else {
        snackbarShowMessage('An error occurred while refreshing user data', 'error', 10000);
      }
    });
  }

  const handleAddFundingSourceSuccess = () => {
    RefreshAuthoritySourceAndUser();
    setShowAddPaymentModal(false);
  };

  const handleVerifyPaymentSuccess = () => {
    snackbarShowMessage('Bank Account verified successfully', 'success', 8000);
    RefreshAuthoritySourceAndUser();
    setShowVerifyPaymentDialog(false);
  }

  const handleCloseRemovePaymentDialog = () => {
    setShowDeletePaymentDialog(false);
  };

  const handlePaymentRemove = () => {
    RefreshAuthoritySourceAndUser();
    setShowDeletePaymentDialog(false);
    snackbarShowMessage('Funding Source removed successfully', 'success', 8000);
  };

  const handlePaymentMenuAction = useCallback((actionName: string) => {
    switch (actionName) {
      case 'remove':
        setShowDeletePaymentDialog(true);
        break;
      case 'verify':
        setShowVerifyPaymentDialog(true);
        break;
      default:
        break;
    }
  }, [setShowDeletePaymentDialog]);

  return (
    <Grid container direction={'column'} className={classes.gap15}>
      <Grid item container justifyContent={'space-between'} className={classes.gap15}>
        <Typography variant={'h5'} component={'h1'}>Payout Bank Account</Typography>
        <EmailVerifyToolTip
          title={"Please verify your email before you can add a bank account."}
          isEmailVerified={account?.emailVerified}
          children={
            <Button
              onClick={openAddPaymentModal}
              className={classes.addPaymentButton}
              color='secondary'
              variant='contained'
              disabled={!account?.emailVerified}
            >
              {`${fundingSourceExists ? "Change" : "Add"} Bank Account`}
            </Button>
          }
        />
      </Grid>
      <PartnerFundingSourcesList
        account={account}
        onMenuAction={handlePaymentMenuAction}
        fundingSourceExists={fundingSourceExists}
      />
      {isMicroDepositVerificationEnabled &&
        <PartnerVerifyBankAccountDialog
          fundingSourceId={fundSourceId}
          accountId={account?.id}
          open={showVerifyPaymentDialog}
          onClose={() => setShowVerifyPaymentDialog(false)}
          handleLogout={handleLogout}
          onSuccess={handleVerifyPaymentSuccess}
        />
      }
      <PartnerAddFundingSourceDialog
        account={account}
        handleLogout={handleLogout}
        open={showAddPaymentModal}
        onClose={() => setShowAddPaymentModal(false)}
        onSuccess={handleAddFundingSourceSuccess}
      />
      <RemovePartnerFundingSourceDialog
        open={showDeletePaymentDialog}
        onClose={handleCloseRemovePaymentDialog}
        handleLogout={handleLogout}
        account={account}
        onSuccess={handlePaymentRemove}
      />
    </Grid>
  );
};

export default withSnackbar(PartnerPaymentMethods);
